import { INTERNAL_NOTE_CHANNEL_ID } from "../../../components/MessageHistory/ChannelSelector";
import i18n from "../../../i18n/config";
import {
  channelTypeRequiresClientPhone,
  hasClientPhoneRegistered,
} from "../../../utils/clientContactUtils";
import {
  sendPersonalWhatsappChatMessage,
  getPersonalChatKeyFromSyntheticConversationId,
} from "../../../api/client";
import { deliveryStatusFromPersonalWhatsappStatus } from "../../../components/MessageHistory/messageDeliveryStatus";
import { uploadOne } from "../../../hooks/useS3Upload";

const fetchBlobFromUrl = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error("Falha ao preparar arquivo de áudio para envio.");
  }
  return await response.blob();
};

export default function useMessageHandler({
  selectedConversation,
  userData,
  setMessages,
  sendMessageMutation,
  updateMutation,
  onUpdateConversation,
}) {
  const getThrownApiMessage = (error) => {
    if (error == null) return null;
    if (typeof error === "string" && error.trim()) return error.trim();
    if (typeof error.message === "string" && error.message.trim()) return error.message.trim();
    const d = error.response?.data;
    if (d && typeof d.message === "string" && d.message.trim()) return d.message.trim();
    return null;
  };

  const getSendErrorMessage = (error) => {
    const code = String(error?.code || "").toLowerCase();
    const status = Number(error?.status || error?.statusCode || 0);
    if (status === 402 || code.startsWith("billing.")) {
      if (code === "billing.limit_disabled") {
        return i18n.t("billingErrors.limitDisabled", { ns: "app" });
      }
      if (typeof error?.message === "string" && error.message.trim()) {
        return error.message;
      }
      return i18n.t("billingErrors.limitExceeded", { ns: "app" });
    }
    if (status === 401 || status === 403) {
      return "Erro de autenticação com o canal. Verifique as credenciais nas configurações.";
    }
    if (status === 404) {
      return "O canal ou conversa não foi encontrado. Atualize a página e tente novamente.";
    }
    if (status === 429) {
      return "Limite de envios atingido. Aguarde alguns minutos e tente reenviar.";
    }
    if (status >= 500) {
      return "Erro no servidor. Aguarde alguns instantes e tente reenviar a mensagem.";
    }
    if (error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT") {
      return "A conexão expirou. Verifique sua internet e tente reenviar.";
    }
    if (!navigator.onLine) {
      return "Sem conexão com a internet. Verifique sua rede e tente reenviar.";
    }
    return null;
  };

  const addOptimisticMessage = (
    tempId,
    { message, file, audioURL, channelType, channel, isImageFile, localImageUrl, location }
  ) => {
    const hasAudio = !!audioURL;
    const hasLocation =
      location &&
      typeof location === "object" &&
      location.latitude != null &&
      location.longitude != null;
    const isInternalNoteDraft = String(channel) === String(INTERNAL_NOTE_CHANNEL_ID);
    const tsNow = Date.now();
    const base = {
      id: tempId,
      text: message || (file ? file.name || "Arquivo enviado" : ""),
      senderName: (userData?.nome || userData?.name || "").trim() || undefined,
      senderId: userData?.id,
      senderType: "agent",
      ts: tsNow,
      timestamp: new Date().toISOString(),
      file: file ? file.name : null,
      audio: hasAudio ? audioURL : null,
      location: hasLocation
        ? {
            latitude: location.latitude,
            longitude: location.longitude,
            lat: location.latitude,
            lng: location.longitude,
            ...(location.name ? { name: location.name } : {}),
            ...(location.address ? { address: location.address } : {}),
          }
        : null,
      // Para anexos enviados via anotação interna, a UI deve renderizar como "note"
      // (mantendo o layout de anotação) mesmo enquanto ainda está "sending".
      type: hasLocation
        ? "location"
        : hasAudio
        ? "audio"
        : isInternalNoteDraft
        ? "note"
        : channelType || "note",
      status: "sending",
      lastUpdate: new Date().toISOString(),
      channel: channelType,
    };
    const enriched =
      isImageFile && localImageUrl
        ? isInternalNoteDraft
          ? { ...base, image: localImageUrl }
          : { ...base, type: "image", image: localImageUrl }
        : { ...base, text: base.text || (hasLocation ? "" : "Arquivo enviado") };
    setMessages((prev) => {
      const updated = new Map(prev);
      updated.set(tempId, enriched);
      return updated;
    });
  };

  const handleSendMessage = async (payload, retryId = null) => {
    if (!selectedConversation?.id) return;

    const isPersonalConv =
      selectedConversation?.kind === "personal" ||
      String(selectedConversation?.id || "").startsWith("wa-personal:");
    const personalChatKey =
      selectedConversation?.personalChatKey ||
      getPersonalChatKeyFromSyntheticConversationId(selectedConversation?.id);

    const {
      message,
      file,
      files,
      audioURL,
      channel,
      channelType,
      transferTo,
      templateId,
      emailTo,
      emailSubject,
      emailCc,
      location: locationPayload,
    } = payload;
    const filesList = Array.isArray(files) && files.length > 0 ? files : file ? [file] : [];

    const hasValidLocation =
      locationPayload &&
      typeof locationPayload === "object" &&
      locationPayload.latitude != null &&
      locationPayload.longitude != null &&
      Number.isFinite(Number(locationPayload.latitude)) &&
      Number.isFinite(Number(locationPayload.longitude)) &&
      !(Number(locationPayload.latitude) === 0 && Number(locationPayload.longitude) === 0);

    const hasSendPayload =
      (message != null && String(message).trim() !== "") ||
      filesList.length > 0 ||
      !!audioURL ||
      !!hasValidLocation;
    const isInternalNote = String(channel) === String(INTERNAL_NOTE_CHANNEL_ID);
    if (
      hasSendPayload &&
      !isInternalNote &&
      !isPersonalConv &&
      channelTypeRequiresClientPhone(channelType) &&
      !hasClientPhoneRegistered(selectedConversation)
    ) {
      return;
    }

    if (transferTo && updateMutation?.mutateAsync && !isPersonalConv) {
      try {
        // Importante: ao transferir para agente, NÃO enviar `id_usuario` (mesmo como `null`),
        // para que o backend não interprete como "remoção explícita de atribuição do usuário".
        // O mesmo vale para transferir para usuário (não enviar `id_agente`).
        await updateMutation.mutateAsync({
          id: selectedConversation.id,
          ...(transferTo.type === "user" ? { id_usuario: transferTo.id } : { id_agente: transferTo.id }),
        });
        const updates =
          transferTo.type === "user"
            ? {
                userId: transferTo.id,
                id_agente: null,
                agentId: null,
                agentName: null,
                userName: transferTo.label ?? null,
                assignedTo: transferTo.label ?? null,
              }
            : {
                userId: null,
                id_agente: transferTo.id,
                agentId: transferTo.id,
                agentName: transferTo.label ?? null,
                userName: null,
                assignedTo: null,
              };
        onUpdateConversation?.(updates);
      } catch (err) {
        console.error("Erro ao transferir solicitação:", err);
      }
    }

    const baseTime = Date.now();
    const sendItems = [];
    if (message != null && String(message).trim()) {
      sendItems.push({ type: "text", message: message.trim() });
    }
    filesList.forEach((f) => sendItems.push({ type: "file", file: f }));
    if (audioURL) {
      sendItems.push({ type: "audio", audioURL });
    }
    if (hasValidLocation) {
      sendItems.push({
        type: "location",
        location: {
          latitude: Number(locationPayload.latitude),
          longitude: Number(locationPayload.longitude),
          ...(locationPayload.name ? { name: String(locationPayload.name) } : {}),
          ...(locationPayload.address ? { address: String(locationPayload.address) } : {}),
        },
      });
    }

    if (sendItems.length === 0) return;

    const applyTemplateAndTransfer = (index) =>
      index === 0
        ? {
            ...(transferTo && { transferTo }),
            ...(templateId != null && { templateId }),
            ...(emailTo != null && { emailTo }),
            ...(emailSubject != null && { emailSubject }),
            ...(emailCc != null && { emailCc }),
          }
        : {};

    for (let i = 0; i < sendItems.length; i++) {
      const item = sendItems[i];
      const tempId = `temp-${baseTime}-${i}`;

      if (retryId && i === 0) {
        setMessages((prev) => {
          const updated = new Map(prev);
          updated.delete(retryId);
          return updated;
        });
      }

      const isImageFile =
        item.type === "file" &&
        item.file &&
        typeof item.file.type === "string" &&
        item.file.type.startsWith("image/");
      const localImageUrl = isImageFile && item.file ? URL.createObjectURL(item.file) : null;

      addOptimisticMessage(tempId, {
        message: item.type === "text" ? item.message : "",
        file: item.type === "file" ? item.file : null,
        audioURL: item.type === "audio" ? item.audioURL : null,
        channelType,
        channel,
        isImageFile,
        localImageUrl,
        location: item.type === "location" ? item.location : null,
      });

      try {
        if (isPersonalConv && !isInternalNote) {
          if (!personalChatKey) {
            console.warn("WhatsApp pessoal: chatKey ausente.");
            continue;
          }
          if (item.type === "location") {
            setMessages((prev) => {
              const updated = new Map(prev);
              const errored = updated.get(tempId);
              if (!errored) return prev;
              updated.set(tempId, {
                ...errored,
                status: "error",
                errorMessage: i18n.t("pane.personalMediaNotSupported", { ns: "chat" }),
              });
              return updated;
            });
            continue;
          }

          let sendPayload;
          if (item.type === "text") {
            sendPayload = { text: item.message };
          } else if (item.type === "audio") {
            const audioBlob = await fetchBlobFromUrl(item.audioURL);
            const mimeType =
              audioBlob.type && audioBlob.type !== "audio/mp3" ? audioBlob.type : "audio/mpeg";
            const fileName = `audio-${Date.now()}.mp3`;
            const audioFile = new File([audioBlob], fileName, { type: mimeType });
            const uploaded = await uploadOne(audioFile, { prefix: "chat" });
            sendPayload = { type: "audio", audio: uploaded.url };
          } else if (item.type === "file" && item.file) {
            const uploaded = await uploadOne(item.file, { prefix: "chat" });
            const isImg = typeof item.file.type === "string" && item.file.type.startsWith("image/");
            const isVid = typeof item.file.type === "string" && item.file.type.startsWith("video/");
            sendPayload = {
              type: isImg ? "image" : isVid ? "video" : "file",
              text: "",
              file: {
                url: uploaded.url,
                name: item.file.name,
                type: item.file.type,
                size: item.file.size,
              },
            };
          } else {
            continue;
          }

          try {
            const result = await sendPersonalWhatsappChatMessage(personalChatKey, sendPayload);
            const saved = result?.data?.message ?? result?.message ?? result?.data;
            setMessages((prev) => {
              const updated = new Map(prev);
              const original = updated.get(tempId);
              if (!original) return prev;
              const newId = saved?.id ?? saved?.insertedId ?? null;
              const createdAt = saved?.ts ? new Date(saved.ts).toISOString() : null;
              const personalDs = deliveryStatusFromPersonalWhatsappStatus(saved?.statusWhatsapp);
              const personalSender = (userData?.nome || userData?.name || "").trim() || original.senderName;
              const waId = saved?.waMessageId != null ? String(saved.waMessageId).trim() : null;
              if (!newId) {
                updated.set(tempId, {
                  ...original,
                  status: "sent",
                  createdAt: createdAt || original.createdAt,
                  deliveryStatus: personalDs,
                  ...(waId ? { waMessageId: waId } : {}),
                  ...(personalSender ? { senderName: personalSender } : {}),
                });
                return updated;
              }
              updated.set(newId, {
                ...original,
                id: newId,
                status: "sent",
                createdAt: createdAt || original.createdAt,
                deliveryStatus: personalDs,
                ...(waId ? { waMessageId: waId } : {}),
                ...(personalSender ? { senderName: personalSender } : {}),
              });
              updated.delete(tempId);
              return updated;
            });
          } catch (err) {
            const msg = getThrownApiMessage(err) || getSendErrorMessage(err) || "Falha ao enviar.";
            setMessages((prev) => {
              const updated = new Map(prev);
              const errored = updated.get(tempId);
              if (!errored) return prev;
              updated.set(tempId, {
                ...errored,
                status: "error",
                errorMessage: msg,
              });
              return updated;
            });
          }
          continue;
        }

        const mutationPayload = {
          conversationId: selectedConversation.id,
          phone: selectedConversation?.celular || "",
          email: selectedConversation?.email || "",
          message: item.type === "text" ? item.message : "",
          file: item.type === "file" ? item.file : undefined,
          audioURL: item.type === "audio" ? item.audioURL : undefined,
          ...(item.type === "location"
            ? {
                location: item.location,
                ...(item.location?.name ? { locationName: item.location.name } : {}),
                ...(item.location?.address ? { locationAddress: item.location.address } : {}),
              }
            : {}),
          channel,
          projectId: selectedConversation.projectId,
          userId: userData?.id,
          ...applyTemplateAndTransfer(i),
        };
        const result = await sendMessageMutation.mutateAsync(mutationPayload);
        const resultData = result?.data ?? result ?? {};
        const sentFileMeta = result?.__sentFile;

        setMessages((prev) => {
          const updated = new Map(prev);
          const original = updated.get(tempId);
          if (!original) return prev;
          if (localImageUrl) URL.revokeObjectURL(localImageUrl);
          const documentFromUpload =
            sentFileMeta?.url != null && String(sentFileMeta.url).trim()
              ? {
                  url: String(sentFileMeta.url).trim(),
                  name: sentFileMeta.name || original.text || "Documento",
                }
              : null;
          const newId = resultData.id ?? resultData.insertedId ?? resultData.messageId ?? resultData?.data?.id;
          const createdAt = resultData.createdAt ?? null;
          const updatedAt = resultData.updatedAt ?? null;
          const deliveryStatus = resultData.deliveryStatus ?? null;
          if (!newId) {
            // fallback: mantém tempId, mas marca como enviado para não ficar preso em "Enviando..."
            updated.set(tempId, {
              ...original,
              status: "sent",
              createdAt: createdAt || original.createdAt,
              updatedAt: updatedAt || original.updatedAt,
              deliveryStatus: deliveryStatus || original.deliveryStatus,
              ...(documentFromUpload ? { document: documentFromUpload } : {}),
            });
            return updated;
          }
          updated.set(newId, {
            ...original,
            id: newId,
            status: "sent",
            createdAt: createdAt || original.createdAt,
            updatedAt: updatedAt || original.updatedAt,
            deliveryStatus: deliveryStatus || original.deliveryStatus,
            ...(documentFromUpload ? { document: documentFromUpload } : {}),
          });
          updated.delete(tempId);
          return updated;
        });
      } catch (error) {
        console.error("Erro ao enviar mensagem:", error);
        // Não revoga localImageUrl para manter o preview visível na mensagem com status "error"
        const friendlyError = getSendErrorMessage(error);
        const apiText = getThrownApiMessage(error);
        setMessages((prev) => {
          const updated = new Map(prev);
          const errored = updated.get(tempId);
          if (!errored) return prev;
          updated.set(tempId, {
            ...errored,
            status: "error",
            errorMessage: friendlyError || apiText || errored.errorMessage || null,
          });
          return updated;
        });
      }
    }
  };

  return { handleSendMessage };
}
